/**
 * Compares `datapathViewVersion` strings to decide whether an incoming
 * entity should overwrite the one already in the slice. Returns true when
 * `newVersion` is strictly newer than `existingVersion`.
 *
 * Datapath emits the version in TWO shapes, sometimes for the same entity
 * type depending on path:
 *   - **colon-delimited**: `"<gen>:<version>:<trailing>"`, e.g.
 *     `"0002:0000000000000000023:0000000000000000000"` — group 1 is a
 *     monotonic GENERATION (stream epoch; observed values include 0001 AND
 *     0002), group 2 is the engine's monotonic `version` integer.
 *   - **colonless 19-digit**: `"0000000000000000023"` — the `version`
 *     integer alone, zero-padded. No generation group.
 *
 * Both shapes encode the same `version` ordering key, plus an optional
 * GENERATION the colon form carries. We parse each side and compare
 * numerically (BigInt — the 19-digit values exceed `Number.MAX_SAFE_INTEGER`,
 * so string/number math would lose precision).
 *
 * Format meaning in practice: the COLON form is what a real WS goal push
 * carries in prod; the COLONLESS form is the optimistic mutation stamp
 * (`applyGoalFromMutation`) — and a Gamma-only WS quirk. So the comparison
 * is DIRECTIONAL when formats are mixed:
 *   - both colon (colon vs colon): generation wins, then version — a higher
 *     generation is a stream re-key that supersedes the prior epoch.
 *   - existing COLONLESS, incoming COLON (real WS over optimistic
 *     placeholder): incoming wins UNCONDITIONALLY. This is "WS truth
 *     replaces placeholder," and it covers generation-rollover — a new
 *     epoch whose version RESET low (gen-2-v1) must still overwrite a stale
 *     optimistic colonless v51.
 *   - existing COLON, incoming COLONLESS (optimistic over an existing WS
 *     entry): compare `version` magnitude — the optimistic stamp beats an
 *     OLDER WS entry (XD-unlink: cleared-deps response must land) but loses
 *     to a newer one, and can't churn an equal-version WS entry.
 *   - both colonless (the all-Gamma cancel path): compare `version`.
 *
 * This is VALUE-based, not format-based. The earlier "colon always wins"
 * rule was format-rigged: it let an optimistic colon stamp beat the newer
 * colonless terminal CANCELED (stuck-pending puck, 2026-06-16) and would
 * conversely drop a colonless optimistic stamp under an older colon WS entry
 * (re-breaking the XD-unlink fix). Comparing `(gen, version)` kills both.
 *
 * **Legacy / default format** (`"0"`, `"001"`, short non-padded strings, or
 * non-numeric junk): falls back to the original length-then-lexicographic
 * comparison. The API seed `"0"` is the canonical legacy value and loses to
 * any parsed `(gen, version)` key with a positive magnitude.
 *
 * @param existingVersion - Current version string
 * @param newVersion - New version string to compare against
 * @returns Boolean indicating if the update should proceed
 */

/**
 * A version's numeric ordering key. `null` ⇒ unparseable (legacy/junk).
 * `generation` is `null` for the colonless form (no gen group) — distinct
 * from generation `0`, so mixed-format comparisons compare `version` only.
 */
interface VersionKey {
  generation: bigint | null;
  version: bigint;
}

/**
 * Parse a `datapathViewVersion` into its `(generation, version)` ordering
 * key, or null when the string isn't a recognized numeric version (legacy
 * `"0"`, short unpadded test strings, non-numeric junk) — those route to the
 * legacy comparison.
 *
 *   - colon form `"<gen>:<version>:..."` → `{ generation: gen, version: g2 }`
 *   - colonless 10+-digit form `"<version>"` → `{ generation: null, version }`
 *     (null, NOT 0 — so mixed colon/colonless compares version only; see
 *     the comparator's "both parse" branch)
 *
 * The 10-digit floor distinguishes a real zero-padded engine version
 * (19 digits in practice) from a short legacy/seed string; a real version
 * is never < 10 digits once padded, and no Datapath path emits an unpadded
 * short colonless version.
 */
const parseVersionKey = (raw: string): VersionKey | null => {
  if (raw.includes(":")) {
    const parts = raw.split(":");
    if (!/^\d+$/.test(parts[0]) || !/^\d+$/.test(parts[1] ?? "")) return null;
    return { generation: BigInt(parts[0]), version: BigInt(parts[1]) };
  }
  // Colonless: only treat a long all-digit string as a real (padded) version.
  // generation = null (not 0) so mixed comparisons compare version only.
  if (/^\d{10,}$/.test(raw)) {
    return { generation: null, version: BigInt(raw) };
  }
  return null;
};

export const shouldUpdateBasedOnVersion = (
  existingVersion: string | undefined,
  newVersion: string | undefined
): boolean => {
  const existing = existingVersion || "0";
  const newer = newVersion || "0";

  const existingKey = parseVersionKey(existing);
  const newerKey = parseVersionKey(newer);

  // Both parse to a numeric key.
  if (existingKey && newerKey) {
    // Both colon (both carry a generation) → generation wins, then version.
    if (existingKey.generation !== null && newerKey.generation !== null) {
      if (newerKey.generation !== existingKey.generation) {
        return newerKey.generation > existingKey.generation;
      }
      return newerKey.version > existingKey.version;
    }

    // Mixed format (one colon, one colonless). The colonless form is the
    // optimistic mutation stamp (`applyGoalFromMutation`); a real WS goal
    // push carries the colon form in prod (colonless is a Gamma-only WS
    // quirk). So an INCOMING colon entry over an EXISTING colonless entry
    // is "real WS truth arriving over an optimistic placeholder" — it wins
    // unconditionally, which also covers the generation-rollover case
    // (gen bump resets version low: gen-2-v1 must still overwrite a stale
    // colonless v51). Per the "WS carries truth, optimistic is placeholder"
    // tenet.
    if (existingKey.generation === null && newerKey.generation !== null) {
      return true;
    }
    // Existing colon, incoming colonless (optimistic) — the optimistic stamp
    // must still beat an OLDER WS colon entry (XD-unlink: cleared-deps
    // updateGoal response at a higher version must land over the earlier WS
    // entry). Compare version magnitude; the optimistic stamp loses to a
    // newer WS but beats an older one.
    return newerKey.version > existingKey.version;
  }

  // Both colon-delimited but at least one didn't parse to a numeric key
  // (non-numeric segment) → original gen-numeric-then-rest-string logic.
  // Real wire versions always parse above; this preserves back-compat for
  // synthetic/legacy colon strings with non-digit segments.
  if (existing.includes(":") && newer.includes(":")) {
    const existingParts = existing.split(":");
    const newerParts = newer.split(":");
    const existingFirst = parseInt(existingParts[0], 10);
    const newerFirst = parseInt(newerParts[0], 10);
    if (isNaN(existingFirst) || isNaN(newerFirst)) {
      return newer > existing;
    }
    if (newerFirst !== existingFirst) {
      return newerFirst > existingFirst;
    }
    return newerParts.slice(1).join(":") > existingParts.slice(1).join(":");
  }

  // Only ONE side is a real version → it wins (a parsed version always beats
  // a legacy/default/junk string like the API seed "0").
  if (newerKey && !existingKey) return true;
  if (existingKey && !newerKey) return false;

  // Neither parses — legacy comparison (length then lexicographic).
  if (newer.length !== existing.length) {
    return newer.length > existing.length;
  }
  return newer > existing;
};
